import { type KeyboardEvent, useCallback, useMemo } from "react";
import type { GestureResponderEvent } from "react-native";
import { useLink } from "solito/link";
import { isWeb } from "tamagui";

/** Solito link options forwarded to `useLink` for web routing. */
export type SolitoLinkOptions = {
  /** Replace the current history entry instead of pushing. */
  replace?: boolean;
  /** Scroll to top on navigation (web). @default true */
  scroll?: boolean;
  /** Perform a shallow route change (web — Next.js). */
  shallow?: boolean;
};

/** Static — no interaction. `disabled` is allowed for visual-only display states (SelectionCard demos, list items showing locked rows). */
export type InteractiveStaticProps = {
  onPress?: never;
  href?: never;
  replace?: never;
  scroll?: never;
  shallow?: never;
  disabled?: boolean;
};

/** Action — required `onPress`, no href. Renders as `<button>` with role + keyboard contract. */
export type InteractiveActionProps = {
  onPress: (event: GestureResponderEvent) => void;
  href?: never;
  replace?: never;
  scroll?: never;
  shallow?: never;
  disabled?: boolean;
};

/** Link — required `href`, optional analytics `onPress`. Renders as `<a>` with Solito routing. */
export type InteractiveLinkProps = SolitoLinkOptions & {
  href: string;
  /** Side-effect callback fired before navigation. Must NOT call `event.preventDefault()` or navigation breaks. */
  onPress?: (event: GestureResponderEvent) => void;
  disabled?: boolean;
};

/** Action or link — the element IS interactive. */
export type InteractiveProps = InteractiveActionProps | InteractiveLinkProps;

/** Static, action, or link — interactivity is optional. */
export type OptionalInteractiveProps =
  | InteractiveStaticProps
  | InteractiveProps;

type UseInteractiveElementParams = {
  onPress?: (event: GestureResponderEvent) => void;
  href?: string;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  disabled?: boolean;
  /** Override the derived interactivity. `false` is the kill switch — handlers are accepted but not wired to the rendered element. */
  interactive?: boolean;
};

/** Props to spread onto an interactive element. Empty when static. */
type InteractiveElementProps = {
  onPress?: (event: GestureResponderEvent) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  tabIndex?: number;
  href?: string;
  "aria-disabled"?: boolean;
};

type UseInteractiveElementResult = {
  /** Drives the boolean `interactive` variant on styled components. */
  isInteractive: boolean;
  /** Tag for tamagui's `render` prop. Disabled links render as `span` since `<a>` without `href` confuses screen readers. Undefined when static. */
  renderTag: "a" | "button" | "span" | undefined;
  /** Props to spread onto the rendered element. */
  interactiveProps: InteractiveElementProps;
};

/**
 * Shared interactive wiring for Card, ListItem, etc. — merges `onPress` with
 * solito navigation, gates on `disabled`, handles Enter/Space activation.
 */
export const useInteractiveElement = ({
  onPress,
  href,
  replace,
  scroll,
  shallow,
  disabled,
  interactive,
}: UseInteractiveElementParams): UseInteractiveElementResult => {
  // `useLink` must always be called (hook rules). When there's no href we
  // pass a placeholder and ignore the result downstream.
  const linkResult = useLink({
    href: href ?? "/",
    replace,
    scroll,
    shallow,
  });

  const isInteractiveResolved =
    interactive ?? (href !== undefined || onPress !== undefined);
  const mode: "link" | "button" | "static" = !isInteractiveResolved
    ? "static"
    : href !== undefined
      ? "link"
      : "button";

  const resolvedOnPress = useCallback(
    (event: GestureResponderEvent) => {
      if (disabled) {
        return;
      }
      onPress?.(event);
      if (href !== undefined) {
        linkResult.onPress?.(event);
      }
    },
    [disabled, href, onPress, linkResult.onPress],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Don't react to events bubbling from interactive children (form fields,
      // nested buttons). Only act on events targeting this element itself.
      if (event.target !== event.currentTarget) {
        return;
      }
      if (disabled) {
        return;
      }
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        // Trigger native click — flows through React's onClick (which RNW
        // wires from onPress) so we don't have to bridge KeyboardEvent →
        // GestureResponderEvent.
        event.currentTarget.click();
      }
    },
    [disabled],
  );

  const interactiveProps = useMemo<InteractiveElementProps>(() => {
    if (mode === "static") {
      return {};
    }
    return {
      onPress: resolvedOnPress,
      onKeyDown,
      tabIndex: disabled ? -1 : 0,
      ...(mode === "link" && isWeb && !disabled ? { href } : {}),
      ...(disabled ? { "aria-disabled": true } : {}),
    };
  }, [mode, resolvedOnPress, onKeyDown, disabled, href]);

  const isInteractive = mode !== "static";
  const renderTag: UseInteractiveElementResult["renderTag"] =
    mode === "link"
      ? disabled
        ? "span"
        : "a"
      : mode === "button"
        ? "button"
        : undefined;

  return { isInteractive, renderTag, interactiveProps };
};

export type { InteractiveElementProps, UseInteractiveElementParams };
